import {
  MicroServiceAPI,
  ErrorNotFound,
  ErrorServerResponse,
  ErrorBadRequest,
} from '@plasma-platform/service-api';
import { Validator } from 'livr';
import extraRules from 'livr-extra-rules';

/**
 * Carts Micro Service API SDK
 *
 * @namespace CartsService
 * @module CartsService
 * @class
 * @exports
 *
 * @param {string} url - service url
 * @param {string | null} [token = null] - user access tocken if available
 * @see https://jsonplaceholder.typicode.com/
 */
export default class CartsService extends MicroServiceAPI {
  /**
   * Constructor of instance
   *
   * @method constructor
   * @name constructor
   * @async
   * @public
   * @memberof CartsService
   * @param {string} url - (required) url to service
   * @param {string} token  - user token
   *
   * @return {object} - service SDK object
   *
   * @example
   * (async () => {
   *   const cartsService = new CartsService('https://jsonplaceholder.typicode.com/', 'dsugsahfdkljhafljkdfhajkldshfad');
   * })();
   */
  constructor(...props) {
    super(...props);
    /**
     * Object with class service messages
     * @type {Object}
     */
    Validator.defaultAutoTrim(true);
    Validator.registerDefaultRules(extraRules);
    this.messages = {
      tokenIsNotDefined: 'token is not defined',
      incorrectRequestParameter: 'incorrect request parameter',
      noRequiredParameter: 'no request parameter',
      badRequest: 'bad request error',
      notFound: 'data not found',
    };
  }

  /**
   * Add new cart
   *
   * @method addNewCart
   * @name addNewCart
   * @async
   * @public
   * @memberof CartsService
   * @param {string} currency - (required) Currency code iso3
   * @param {string} locale - cart locale
   * @param {object} attributes  - List of predefined attributes to be stored with cart
   * @param {string} attributes.affiliate - Affiliate name
   * @param {string} attributes.affiliateAuthor - AffiliateAuthor name
   * @param {string} attributes.operator - Operator name
   * @param {string} attributes.description - Description for cart
   * @param {string} attributes.channel - Channel name (create_cart)
   * @param {object} attributes.trackers - Analytics trackers
   * @param {string} attributes.trackers[ga] - Google Analytics tracker
   * @param {string} attributes.trackers[ad] - Google Adwords tracker
   * @param {string} attributes.trackers[sc] - Soft Cube tracker
   *
   * @return {object} - object with new cart properties
   *
   * @example
   * (async () => {
   *   const cartsService = new CartsService('https://jsonplaceholder.typicode.com/', 'dsugsahfdkljhafljkdfhajkldshfad');
   *   const cart = await cartsService.addNewCart('USD', { affiliate: 'colorlib' });
   * })();
   */
  addNewCart = async (currency, attributes = {}, locale = 'en') => {
    const validator = new Validator({
      currency: ['required', 'string'],
      affiliate: ['string'],
      affiliateAuthor: ['string'],
      operator: ['string'],
      description: ['string'],
      channel: ['string'],
      locale: ['string'],
      trackers: {
        nested_object: {
          ga: 'string',
          ad: 'string',
          sc: 'string',
        },
      },
    });
    const data = validator.validate({
      currency,
      locale,
      ...attributes,
    });
    const errors = validator.getErrors();
    if (errors) {
      throw new ErrorBadRequest(JSON.stringify(errors));
    }

    const response = await this.request(`${this.url}carts`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        Accept: 'application/json',
      },
      body: {
        ...data,
        attributes: {
          affiliate: attributes.affiliate || 'TM',
          affiliateAuthor: attributes.affiliateAuthor || 'TM',
          operator: attributes.operator || '',
          description: attributes.description || '',
          channel: attributes.channel || '',
        },
      },
    });

    if (response.ok) {
      const json = await response.json();
      return json;
    }

    if (response.status === 400) {
      throw new ErrorBadRequest(this.messages.badRequest);
    }

    if (response.status === 404) {
      throw new ErrorNotFound(this.messages.notFound);
    }

    throw new ErrorServerResponse(response.status, response.statusText);
  };

  /**
   * Clone cart
   *
   * @method cloneCart
   * @name cloneCart
   * @async
   * @public
   * @memberof CartsService
   * @param {string} id - (required) cart id for cone
   *
   * @return {object} - object with new cart properties
   *
   * @example
   * (async () => {
   *   const cartsService = new CartsService('https://jsonplaceholder.typicode.com/', 'hfkjasdhfljkadhflkajdshflkadjhald');
   *   const cart = await cartsService.cloneCart(111);
   * })();
   */
  cloneCart = async params => {
    const validator = new Validator({
      cartId: ['required', 'string'],
      userId: 'integer',
    });
    const validData = validator.validate({...params});
    const errors = validator.getErrors();
    if (errors) {
      throw new ErrorBadRequest(JSON.stringify(errors));
    }
    if(params.userId) {
      validData.user_id = params.userId;
    }
    const response = await this.request(`${this.url}carts/${params.cartId}`, {
      method: 'COPY',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        Accept: 'application/json',
      },
      body: {
        ...validData
      },
    });
    if (response.ok) {
      const json = await response.json();
      return json;
    }
    if (response.status === 400) {
      throw new ErrorBadRequest(this.messages.badRequest);
    }

    if (response.status === 404) {
      throw new ErrorNotFound(this.messages.notFound);
    }

    throw new ErrorServerResponse(response.status, response.statusText);
  };

  /**
   * Get cart by id
   *
   * @method getCartById
   * @name getCartById
   * @async
   * @public
   * @memberof CartsService
   * @param {string} id - (required) Cart id
   *
   * @return {object} - object with cart properties
   *
   * @example
   * (async () => {
   *   const cartsService = new CartsService('https://jsonplaceholder.typicode.com/');
   *   const cart = await cartsService.getCartById(111);
   * })();
   */
  getCartById = async id => {
    const validator = new Validator({
      id: ['required', 'string'],
    });
    const data = validator.validate({
      id,
    });
    const errors = validator.getErrors();
    if (errors) {
      throw new ErrorBadRequest(JSON.stringify(errors));
    }

    const response = await this.request(`${this.url}carts/${data.id}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        Accept: 'application/json',
      },
    });

    if (response.ok) {
      const json = await response.json();
      return json;
    }

    if (response.status === 400) {
      throw new ErrorBadRequest(this.messages.badRequest);
    }

    if (response.status === 404) {
      throw new ErrorNotFound(this.messages.notFound);
    }

    throw new ErrorServerResponse(response.status, response.statusText);
  };

  /**
   * Get cart calculated by id
   *
   * @method getCartCalculatedById
   * @name getCartCalculatedById
   * @async
   * @public
   * @memberof CartsService
   * @param {string} id - (required) Cart id
   *
   * @return {object} - object with cart properties
   *
   * @example
   * (async () => {
   *   const cartsService = new CartsService('https://jsonplaceholder.typicode.com/');
   *   const cart = await cartsService.getCartCalculatedById(111);
   * })();
   */
  getCartCalculatedById = async id => {
    const validator = new Validator({
      id: ['required', 'string'],
      token: ['required', 'string'],
    });
    const data = validator.validate({
      token: this.token,
      id,
    });
    const errors = validator.getErrors();
    if (errors) {
      throw new ErrorBadRequest(JSON.stringify(errors));
    }

    const response = await this.request(`${this.url}carts/${data.id}/calculate`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        Accept: 'application/json',
      },
    });

    if (response.ok) {
      const json = await response.json();
      return json;
    }

    if (response.status === 400) {
      throw new ErrorBadRequest(this.messages.badRequest);
    }

    if (response.status === 404) {
      throw new ErrorNotFound(this.messages.notFound);
    }

    throw new ErrorServerResponse(response.status, response.statusText);
  };

  /**
   * Get my carts
   *
   * @method getMyCarts
   * @name getMyCarts
   * @async
   * @public
   * @memberof CartsService
   *
   * @param {string} typeSort - type of sorting products in cart
   *
   * @return {array} - array with objects cart properties
   *
   * @example
   * (async () => {
   *   const cartsService = new CartsService('https://jsonplaceholder.typicode.com/');
   *   const cart = await cartsService.getMyCarts();
   * })();
   */
  getMyCarts = async typeSort => {
    const validator = new Validator({
      token: ['required', 'string'],
    });
    validator.validate({ token: this.token });
    const errors = validator.getErrors();
    if (errors) {
      throw new ErrorBadRequest(this.messages.tokenIsNotDefined);
    }

    const sort = typeSort ? `?sort=${typeSort}` : '';

    const response = await this.request(`${this.url}carts/my${sort}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        Accept: 'application/json',
      },
    });

    if (response.ok) {
      const json = await response.json();
      return json;
    }

    if (response.status === 400) {
      throw new ErrorBadRequest(this.messages.badRequest);
    }

    if (response.status === 404) {
      throw new ErrorNotFound(this.messages.notFound);
    }

    throw new ErrorServerResponse(response.status, response.statusText);
  };

  /**
   * Link item to cart
   * @method linkItemToCart
   * @name linkItemToCart
   * @async
   * @public
   * @memberof CartsService
   * @param {string} productServiceLink - (required) link to product in service products
   * @param {string} cartId - (required) cart id
   * @param {string} itemType - (required) type of product (products | services | discounts | externalProducts)
   * @param {object} data - (required) params for link product to cart
   * @param {number} data.license - license id
   * @param {string} data.channel - channel for service (required if itemType is services)
   * @param {string} data.locale - users locale
   * @param {number} data.position - position for service
   * @param {string} data.presentation_id - presentation id for service
   * @return {object} - object cart properties
   * @example
   * (async () => {
   *   const cartsService = new CartsService('https://jsonplaceholder.typicode.com/');
   *   const cart = await cartsService.linkItemToCart('http://service-products.dev/api/v1/products/en/55555', 111, 'products', {
   *     license: 50,
   *   });
   * })();
   */
  linkItemToCart = async (productServiceLink, cartId, itemType, data) => {
    const validator = new Validator({
      productServiceLink: ['required', 'string'],
      cartId: ['required', 'string'],
      itemType: [
        'required',
        'string',
        {
          one_of: [
            'products',
            'services',
            'memberships',
            'discounts',
            'externalProducts',
            'support',
          ],
        },
      ],
      data: {
        nested_object: {
          license: { or: ['string', 'integer' ]},
          channel: [
            'string',
            { required_if: { itemType: 'services' } },
            {
              one_of: [
                'tm-cart',
                'tm-preview',
                'tm-delivery',
                'tm-pre-sales-cart',
                'tm2-cart',
                'tm2-preview',
                'tm2-order-status-aprove',
                'tm2-popup_preview',
                'mts-cart',
                'mts-preview',
                'mts-delivery',
                'monster-delivery',
                'plasma-cart-banner',
                'plasma-oncart-suggestion',
              ],
            },
          ],
          locale: ['string'],
        },
      },
      position: ['integer'],
      presentation_id: ['integer'],
    });

    const validData = validator.validate({
      productServiceLink,
      cartId,
      itemType,
      data,
    });
    const errors = validator.getErrors();
    if (errors) {
      throw new ErrorBadRequest(this.messages.tokenIsNotDefined);
    }

    if (!validData.data.license) {
      delete validData.data.license;
    }
    if (!validData.data.locale) {
      delete validData.data.locale;
    }

    const response = await this.request(
      `${this.url}carts/${validData.cartId}/${validData.itemType}`,
      {
        method: 'LINK',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          Accept: 'application/json',
          link: `${validData.productServiceLink}; rel="item"`,
        },
        data: { ...validData },
      },
    );

    if (response.ok) {
      const json = await response.json();
      return json;
    }

    if (response.status === 400) {
      throw new ErrorBadRequest(this.messages.badRequest);
    }

    if (response.status === 404) {
      throw new ErrorNotFound(this.messages.notFound);
    }

    throw new ErrorServerResponse(response.status, response.statusText);
  };

  /**
   * Link item to item
   *
   * @method linkItemToItem
   * @name linkItemToItem
   * @async
   * @public
   * @memberof CartsService
   *
   * @param {string} productServiceLink - (required) link to product in service products
   * @param {string} cartId - (required) cart id
   * @param {number} itemId - (required) item id
   * @param {object} data - (required) params for link product to cart
   * @param {string} data.channel - (required) channel for service
   * @param {string} data.locale - users locale
   * @param {number} data.position - position for service
   * @param {string} data.presentation_id - presentation id for service
   * @param {string} typeLink - type of link to item
   *
   * @return {object} - object cart properties
   *
   * @example
   * (async () => {
   *   const cartsService = new CartsService('https://jsonplaceholder.typicode.com/');
   *   const cart = await cartsService.linkItemToItem('http://service-services.dev/api/v1/services/en/1', 111, 222, {
   *     channel: 'tm-cart'
   *   }, 'bundles');
   * })();
   */
  linkItemToItem = async (productServiceLink, cartId, itemId, data, typeLink = 'services') => {
    const validator = new Validator({
      productServiceLink: ['required', 'string'],
      cartId: ['required', 'string'],
      itemId: ['required', 'positive_integer'],
      channel: {
        one_of: [
          'tm-cart',
          'tm-preview',
          'tm-delivery',
          'tm-pre-sales-cart',
          'tm2-cart',
          'tm2-preview',
          'tm2-order-status-aprove',
          'tm2-popup_preview',
          'mts-cart',
          'mts-preview',
          'mts-delivery',
          'tm2-live_demo',
        ],
      },
      position: ['integer'],
      presentation_id: ['integer'],
      typeLink: ['string'],
      locale: ['string'],
    });
    const validData = validator.validate({
      productServiceLink,
      cartId,
      itemId,
      ...data,
    });
    const errors = validator.getErrors();
    if (errors) {
      throw new ErrorBadRequest(this.messages.tokenIsNotDefined);
    }

    const response = await this.request(
      `${this.url}carts/${validData.cartId}/products/${validData.itemId}/${validData.typeLink ||
        typeLink}`,
      {
        method: 'LINK',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          Accept: 'application/json',
          link: `${validData.productServiceLink}; rel="item"`,
        },
        data: { data },
      },
    );

    if (response.ok) {
      const json = await response.json();
      return json;
    }

    if (response.status === 400) {
      throw new ErrorBadRequest(this.messages.badRequest);
    }

    if (response.status === 404) {
      throw new ErrorNotFound(this.messages.notFound);
    }

    throw new ErrorServerResponse(response.status, response.statusText);
  };

  /**
   * Link service to bundle
   *
   * @method linkServiceToBundle
   * @name linkServiceToBundle
   * @async
   * @public
   * @memberof CartsService
   *
   * @param {string} productServiceLinks - (required) link to service in service products
   * @param {string} cartId - (required) cart id
   * @param {number} itemId - (required) item id
   * @param {object} data - (required) params for link product to cart
   * @param {string} data.channel - (required) channel for service
   * @param {string} bundleId - bundle's id
   *
   * @return {object} - object cart properties
   *
   * @example
   * (async () => {
   *   const cartsService = new CartsService('https://jsonplaceholder.typicode.com/');
   *   const cart = await cartsService.linkItemToItem('http://service-services.dev/api/v1/services/en/1', 111, 222, {
   *     channel: 'tm-cart'
   *   }, 'qwerty123');
   * })();
   */
  linkServiceToBundle = async (productServiceLinks, cartId, itemId, data, bundleId) => {
    const validator = new Validator({
      productServiceLinks: ['required', 'string'],
      cartId: ['required', 'string'],
      itemId: ['required', 'positive_integer'],
      channel: {
        one_of: [
          'tm-cart',
          'tm-preview',
          'tm-delivery',
          'tm-pre-sales-cart',
          'tm2-cart',
          'tm2-preview',
          'tm2-order-status-aprove',
          'tm2-popup_preview',
          'mts-cart',
          'mts-preview',
          'mts-delivery',
        ],
      },
      bundleId: ['required', 'string'],
    });
    const validData = validator.validate({
      productServiceLinks,
      cartId,
      itemId,
      bundleId,
      ...data,
    });
    const errors = validator.getErrors();
    if (errors) {
      throw new ErrorBadRequest(JSON.stringify(errors));
    }

    const response = await this.request(
      `${this.url}carts/${validData.cartId}/products/${validData.itemId}/bundles/${
        validData.bundleId
      }/services`,
      {
        method: 'LINK',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          Accept: 'application/json',
          link: `${validData.productServiceLinks}; rel="item"`,
        },
        data: { data: validData },
      },
    );

    if (response.ok) {
      const json = await response.json();
      return json;
    }

    if (response.status === 400) {
      throw new ErrorBadRequest(this.messages.badRequest);
    }

    if (response.status === 404) {
      throw new ErrorNotFound(this.messages.notFound);
    }

    throw new ErrorServerResponse(response.status, response.statusText);
  };

  /**
   * Unlink service from bundle
   *
   * @method unlinkServiceFromBundle
   * @name unlinkServiceFromBundle
   * @async
   * @public
   * @memberof CartsService
   *
   * @param {string} productServiceLinks - (required) link to service in service products
   * @param {string} cartId - (required) cart id
   * @param {number} itemId - (required) item id
   * @param {string} bundleId - bundle's id
   *
   * @return {object} - object cart properties
   *
   * @example
   * (async () => {
   *   const cartsService = new CartsService('https://jsonplaceholder.typicode.com/');
   *   const cart = await cartsService.unlinkServiceFromBundle('http://service-services.dev/api/v1/services/en/1', 111, 222, 'qwerty123');
   * })();
   */
  unlinkServiceFromBundle = async (productServiceLinks, cartId, itemId, bundleId) => {
    const validator = new Validator({
      productServiceLinks: ['required', 'string'],
      cartId: ['required', 'string'],
      itemId: ['required', 'positive_integer'],
      bundleId: ['required', 'string'],
    });
    const data = validator.validate({
      productServiceLinks,
      cartId,
      itemId,
      bundleId,
    });
    const errors = validator.getErrors();
    if (errors) {
      throw new ErrorBadRequest(JSON.stringify(errors));
    }

    const response = await this.request(
      `${this.url}carts/${data.cartId}/products/${data.itemId}/bundles/${data.bundleId}/services`,
      {
        method: 'UNLINK',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          Accept: 'application/json',
          link: `${data.productServiceLinks}`,
        },
      },
    );

    if (response.ok) {
      const json = await response.json();
      return json;
    }

    if (response.status === 400) {
      throw new ErrorBadRequest(this.messages.badRequest);
    }

    if (response.status === 404) {
      throw new ErrorNotFound(this.messages.notFound);
    }

    throw new ErrorServerResponse(response.status, response.statusText);
  };

  /**
   * Merge carts
   *
   * @method mergeCarts
   * @name mergeCarts
   * @async
   * @public
   * @memberof CartsService
   * @param {string} users_cart_id - (required) users cart id
   * @param {string} anonymous_cart_id  - (required) anonymous cart id
   *
   * @return {object} - object with new cart properties
   *
   * @example
   * (async () => {
   *   const cartsService = new CartsService('https://jsonplaceholder.typicode.com/', 'dsugsahfdkljhafljkdfhajkldshfad');
   *   const cart = await cartsService.mergeCarts(111, 333);
   * })();
   */
  mergeCarts = async ({ users_cart_id, anonymous_cart_id }) => { // eslint-disable-line camelcase
    const validator = new Validator({
      users_cart_id: ['required', 'string'],
      anonymous_cart_id: ['required', 'string'],
      token: ['required', 'string'],
    });
    const data = validator.validate({
      users_cart_id,
      anonymous_cart_id,
      token: this.token,
    });
    const errors = validator.getErrors();
    if (errors) {
      throw new ErrorBadRequest(JSON.stringify(errors));
    }

    const response = await this.request(
      `${this.url}carts/${data.users_cart_id}/${data.anonymous_cart_id}`,
      {
        method: 'COPY',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          Accept: 'application/json',
        },
      },
    );

    if (response.ok) {
      const json = await response.json();
      return json;
    }

    if (response.status === 400) {
      throw new ErrorBadRequest(this.messages.badRequest);
    }

    if (response.status === 404) {
      throw new ErrorNotFound(this.messages.notFound);
    }

    throw new ErrorServerResponse(response.status, response.statusText);
  };

  /**
   * Unlink item from cart
   * @method unLinkItemFromCart
   * @name unLinkItemFromCart
   * @async
   * @public
   * @memberof CartsService
   * @param {string} productServiceLink - (required) link to product in service products
   * @param {string} cartId - (required) cart id
   * @param {string} itemType - (required) type of product (products | services | discounts | externalProducts)
   * @return {object} - object cart properties
   * @example
   * (async () => {
   *   const cartsService = new CartsService('https://jsonplaceholder.typicode.com/');
   *   const cart = await cartsService.unLinkItemFromCart('http://service-products.dev/api/v1/products/en/55555', 111, 'products');
   * })();
   */
  unLinkItemFromCart = async (productServiceLink, cartId, itemType) => {
    const validator = new Validator({
      productServiceLink: ['required', 'string'],
      cartId: ['required', 'string'],
      itemType: [
        'required',
        {
          one_of: [
            'products',
            'services',
            'memberships',
            'discounts',
            'externalProducts',
            'support',
          ],
        },
      ],
    });
    const data = validator.validate({
      productServiceLink,
      cartId,
      itemType,
    });
    const errors = validator.getErrors();
    if (errors) {
      throw new ErrorBadRequest(JSON.stringify(errors));
    }

    const response = await this.request(`${this.url}carts/${data.cartId}/${data.itemType}`, {
      method: 'UNLINK',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        Accept: 'application/json',
        link: `${data.productServiceLink}; rel="item"`,
      },
    });

    if (response.ok) {
      const json = await response.json();
      return json;
    }

    if (response.status === 400) {
      throw new ErrorBadRequest(this.messages.badRequest);
    }

    if (response.status === 404) {
      throw new ErrorNotFound(this.messages.notFound);
    }

    throw new ErrorServerResponse(response.status, response.statusText);
  };

  /**
   * Unlink item from item
   *
   * @method unLinkItemFromItem
   * @name unLinkItemFromItem
   * @async
   * @public
   * @memberof CartsService
   *
   * @param {string} productServiceLink - (required) link to product in service products
   * @param {string} cartId - (required) cart id
   * @param {string} itemTypeTarget - (required) item type target
   * @param {number} itemId - (required) item id
   * @param {string} itemTypeSource - (required) item type source
   *
   * @return {object} - object cart properties
   *
   * @example
   * (async () => {
   *   const cartsService = new CartsService('https://jsonplaceholder.typicode.com/');
   *   const cart = await cartsService.unLinkItemFromItem('http://service-services.dev/api/v1/services/en/1', 111, 'services', 222, 'services');
   * })();
   */
  unLinkItemFromItem = async (
    productServiceLink,
    cartId,
    itemTypeTarget,
    itemId,
    itemTypeSource,
  ) => {
    const avaliableTypes = ['products', 'services', 'memberships', 'discounts', 'link-discounts', 'bundles', 'support'];
    const validator = new Validator({
      productServiceLink: ['required', 'string'],
      cartId: ['required', 'string'],
      itemTypeTarget: [
        'required',
        {
          one_of: avaliableTypes,
        },
      ],
      itemId: ['required', 'positive_integer'],
      itemTypeSource: [
        'required',
        {
          one_of: avaliableTypes,
        },
      ],
    });
    const data = validator.validate({
      productServiceLink,
      cartId,
      itemTypeTarget,
      itemId,
      itemTypeSource,
    });
    const errors = validator.getErrors();
    if (errors) {
      throw new ErrorBadRequest(JSON.stringify(errors));
    }

    const response = await this.request(
      `${this.url}carts/${data.cartId}/${data.itemTypeTarget}/${data.itemId}/${
        data.itemTypeSource
      }`,
      {
        method: 'UNLINK',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          Accept: 'application/json',
          link: `${data.productServiceLink}; rel="item"`,
        },
      },
    );

    if (response.ok) {
      const json = await response.json();
      return json;
    }

    if (response.status === 400) {
      throw new ErrorBadRequest(this.messages.badRequest);
    }

    if (response.status === 404) {
      throw new ErrorNotFound(this.messages.notFound);
    }

    throw new ErrorServerResponse(response.status, response.statusText);
  };

  /**
   * update cart
   *
   * @method updateCart
   * @name updateCart
   * @async
   * @public
   * @memberof CartsService
   * @param {string} id - (required) cart id for cone
   * @param {string} currency - currency for shopping cart (iso3)
   * @param {string} locale - cart locale
   * @param {object} attributes - list of predefined attributes to be stored with cart
   * @param {string} attributes.affiliate - Affiliate name
   * @param {string} attributes.affiliateAuthor - AffiliateAuthor name
   * @param {string} attributes.operator - Operator name
   * @param {string} attributes.description - Description for cart
   * @param {string} attributes.channel - Channel name (create_cart)
   * @param {object} attributes.trackers - Analytics trackers
   * @param {string} attributes.trackers[ga] - Google Analytics tracker
   * @param {string} attributes.trackers[ad] - Google Adwords tracker
   * @param {string} attributes.trackers[sc] - Soft Cube tracker
   *
   * @return {object} - object with new cart properties
   *
   * @examp
   * (async () => {
   *   const cartsService = new CartsService('https://jsonplaceholder.typicode.com/', 'hfkjasdhfljkadhflkajdshflkadjhald');
   *   const cart = await cartsService.updateCart('USD', { affiliate: 'colorlib' });
   * })();
   */
  updateCart = async (id, currency = 'USD', attributes = {}, locale = 'en') => {
    const validator = new Validator({
      id: ['required', 'string'],
      currency: ['required', 'string'],
      locale: ['string'],
      affiliate: ['string'],
      affiliateAuthor: ['string'],
      operator: ['string'],
      description: ['string'],
      channel: ['string'],
      trackers: {
        nested_object: {
          ga: 'string',
          ad: 'string',
          sc: 'string',
        },
      },
      token: ['string'],
    });
    const data = validator.validate({
      id,
      currency,
      locale,
      ...attributes,
      token: this.token,
    });
    const errors = validator.getErrors();
    if (errors) {
      throw new ErrorBadRequest(JSON.stringify(errors));
    }

    const dataAttributes = {};

    if (attributes.affiliate) {
      dataAttributes.affiliate = attributes.affiliate;
    }

    if (attributes.affiliateAuthor) {
      dataAttributes.affiliateAuthor = attributes.affiliateAuthor;
    }

    if (attributes.operator) {
      dataAttributes.operator = attributes.operator;
    }

    if (attributes.description) {
      dataAttributes.description = attributes.description;
    }

    if (attributes.channel) {
      dataAttributes.channel = attributes.channel;
    }

    const response = await this.request(`${this.url}carts/${data.id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        Accept: 'application/json',
      },
      body: {
        ...data,
        attributes: {
          ...dataAttributes,
        },
      },
    });

    if (response.ok) {
      const json = await response.json();
      return json;
    }

    if (response.status === 400) {
      throw new ErrorBadRequest(this.messages.badRequest);
    }

    if (response.status === 404) {
      throw new ErrorNotFound(this.messages.notFound);
    }

    throw new ErrorServerResponse(response.status, response.statusText);
  };

  /**
   * Reassign cart
   *
   * @method reassignCart
   * @name reassignCart
   * @async
   * @public
   * @memberof CartsService
   * @param {string} cardId - (required) cart Id
   * @param {string} link - (required) link to user profile
   *
   * @return {object} - object with new cart properties
   *
   * @example
   * (async () => {
   *   const cartsService = new CartsService('https://jsonplaceholder.typicode.com/', 'dsugsahfdkljhafljkdfhajkldshfad');
   *   const cart = await cartsService.reassignCart('123');
   * })();
   */
  reassignCart = async (cardId, link) => {
    const validator = new Validator({
      cardId: ['required', 'string'],
      link: ['required', 'string'],
      token: ['required', 'string'],
    });
    const data = validator.validate({
      cardId,
      link,
      token: this.token,
    });
    const errors = validator.getErrors();
    if (errors) {
      throw new ErrorBadRequest(JSON.stringify(errors));
    }

    const url = `${this.url}carts/${data.cardId}/reassign`;
    const response = await this.request(url, {
      method: 'LINK',
      headers: {
        link: data.link,
        rel: 'author',
      },
    });

    if (response.ok) {
      const json = await response.json();
      return json;
    }

    if (response.status === 400) {
      throw new ErrorBadRequest(this.messages.badRequest);
    }

    throw new ErrorServerResponse(response.status, response.statusText);
  };

  /**
   * Get token for centrifuge
   *
   * @method getToken
   * @name getToken
   * @async
   * @public
   * @memberof CartsService
   * @param {object} options
   * @param {string} options.id - (required) cart Id
   * @param {number} options.time - (required) timestamp for centrifuge
   *
   * @return {object} - object with new cart properties
   * @throws ErrorBadRequest
   *
   * @example <caption>Get token for centrifuge<caption>
   * import Config from 'Config';
   * import CartsService from '@plasma-platform/tm-service-carts';
   * import TMCentrifuge from '@plasma-platform/tm-centrifuge';
   * (async () => {
   *   const cartsService = new CartsService('//socket.templatemonster.com/', 'ZW7NJyKVEtjyGCjPBXOZyQrjNZ5aOjIcJD7SAFNm');
   *   const timestamp = Math.round(new Date().getTime() / 1000);
   *   const [ cart ] = await cartsService.getMyCarts('-created_at&per-page=1');
   *   const token = await cartsService.getToken({
   *      id: cart.id,
   *      timestamp,
   *    });
   *   const centrifuge = TMCentrifuge({
   *      url: Config.centrifugeCartUrl,
   *      timestamp,
   *   });
   *
   *   centrifuge.subscribe('update', (message)=>{…});
   * })();
   */
  getToken = async ({ id, timestamp }) => {
    const validator = new Validator({
      id: ['required', 'string'],
      timestamp: ['required', 'integer'],
    });
    const data = validator.validate({ id, timestamp });
    const errors = validator.getErrors();
    if (errors) {
      throw new ErrorBadRequest(JSON.stringify(errors));
    }

    const response = await this.request(`${this.url}sockets/token/${data.id}/${data.timestamp}`, {
      method: 'POST',
    });

    if (response.ok) {
      const json = await response.json();
      return json;
    }

    throw new ErrorServerResponse(response.status, response.statusText);
  };
}
